import type { TabletopCanvasInputState } from "../controls/TabletopCanvasInputContext";

import Flexbox from "../../../common/components/layout/Flexbox/Flexbox";
import { TabletopCanvasInputMode } from "../controls/TabletopCanvasInputMode";
import {
    TabletopCanvasInputDomain,
    fromTabletopCanvasInputMode,
} from "../controls/TabletopCanvasInputDomain";
import {
    DefaultCanvasInputState,
    TabletopCanvasInputContext,
} from "../controls/TabletopCanvasInputContext";
import { useContext, useMemo, useState } from "react";

import navCrossIcon from "../../../assets/images/common/png/nav_cross.png";
import drawPolygonIcon from "../../../assets/images/common/png/draw_polygon.png";
import selectItemIcon from "../../../assets/images/common/png/select_item.png";
import gridIcon from "../../../assets/images/common/png/grid.png";
import tokenIcon from "../../../assets/images/common/png/token.png";
import tokenAddIcon from "../../../assets/images/common/png/token_add.png";
import Button from "../../../common/components/input/Button/Button";
import ITabletopShapeRenderer from "../objects/element/shape_renderer/ITabletopShapeRenderer";

import paper from "../../../assets/images/tabletop/png/paper.png";
import grass from "../../../assets/images/tabletop/png/grass.png";
import stone from "../../../assets/images/tabletop/png/stone.png";
import TabletopSimpleShapeRenderer from "../objects/element/shape_renderer/TabletopSimpleShapeRenderer";
import LoadCharacterModal from "../../character/modals/LoadCharacterModal";

export default function TabletopEditorToolbar(props: {
    className?: string;
    style?: React.CSSProperties;
}): React.ReactElement {
    const { className, style } = props;

    const { state: canvasInputState, setState: setCanvasInputState } =
        useContext(TabletopCanvasInputContext);

    const [showLoadCharacterModal, setShowLoadCharacterModal] =
        useState<boolean>(false);

    const terrainRenderers: Array<ITabletopShapeRenderer> = [
        new TabletopSimpleShapeRenderer({
            id: "paper",
            outlineWidth: 3,
            outlineStyle: "black",
            fillTextureSrc: paper,
        }),
        new TabletopSimpleShapeRenderer({
            id: "grass",
            outlineWidth: 3,
            outlineStyle: "black",
            fillTextureSrc: grass,
        }),
        new TabletopSimpleShapeRenderer({
            id: "stone",
            outlineWidth: 3,
            outlineStyle: "black",
            fillTextureSrc: stone,
        }),
    ];

    return (
        <div style={style}>
            <Flexbox
                direction="row"
                width="full"
                alignment="end"
                style={{ padding: 24 }}
            >
                <Flexbox
                    className={className}
                    direction="column"
                    gap={8}
                    style={{ width: 120, flexShrink: 1 }}
                >
                    <Flexbox direction="row" gap={8}>
                        <Button
                            onClick={() => {
                                setCanvasInputState({
                                    ...canvasInputState,
                                    inputDomain: fromTabletopCanvasInputMode(
                                        TabletopCanvasInputMode.NAVIGATE_CANVAS,
                                    ),
                                    inputMode:
                                        TabletopCanvasInputMode.NAVIGATE_CANVAS,
                                });
                            }}
                            color={
                                canvasInputState.inputMode ===
                                TabletopCanvasInputMode.NAVIGATE_CANVAS
                                    ? "active"
                                    : undefined
                            }
                            tooltip="Navigation"
                            icon={navCrossIcon}
                        />
                        <Button
                            color={
                                canvasInputState.inputDomain ===
                                TabletopCanvasInputDomain.TERRAIN
                                    ? "active"
                                    : undefined
                            }
                            icon={gridIcon}
                            tooltip="Terrain"
                            onClick={() => {
                                if (
                                    canvasInputState.inputDomain !==
                                    TabletopCanvasInputDomain.TERRAIN
                                ) {
                                    setCanvasInputState({
                                        ...canvasInputState,
                                        inputDomain:
                                            TabletopCanvasInputDomain.TERRAIN,
                                        inputMode:
                                            TabletopCanvasInputMode.MANIPULATE_TERRAIN,
                                    });
                                }
                            }}
                        />
                        <Button
                            color={
                                canvasInputState.inputDomain ===
                                TabletopCanvasInputDomain.TOKENS
                                    ? "active"
                                    : undefined
                            }
                            icon={tokenIcon}
                            tooltip="Tokens"
                            onClick={() => {
                                if (
                                    canvasInputState.inputDomain !==
                                    TabletopCanvasInputDomain.TOKENS
                                ) {
                                    setCanvasInputState({
                                        ...canvasInputState,
                                        inputDomain:
                                            TabletopCanvasInputDomain.TOKENS,
                                        inputMode:
                                            TabletopCanvasInputMode.MANIPULATE_TOKEN,
                                    });
                                }
                            }}
                        />
                    </Flexbox>
                    <hr />
                    <Flexbox direction="row" wrap={true}>
                        {canvasInputState.inputDomain ===
                            TabletopCanvasInputDomain.TERRAIN && (
                            <Button
                                onClick={() => {
                                    setCanvasInputState({
                                        ...canvasInputState,
                                        inputDomain:
                                            fromTabletopCanvasInputMode(
                                                TabletopCanvasInputMode.MANIPULATE_TERRAIN,
                                            ),
                                        inputMode:
                                            TabletopCanvasInputMode.MANIPULATE_TERRAIN,
                                    });
                                }}
                                color={
                                    canvasInputState.inputMode ===
                                    TabletopCanvasInputMode.MANIPULATE_TERRAIN
                                        ? "active"
                                        : undefined
                                }
                                icon={selectItemIcon}
                                tooltip="Manipulate Terrain"
                            />
                        )}
                        {canvasInputState.inputDomain ===
                            TabletopCanvasInputDomain.TERRAIN && (
                            <Button
                                onClick={() => {
                                    setCanvasInputState({
                                        ...canvasInputState,
                                        inputDomain:
                                            fromTabletopCanvasInputMode(
                                                TabletopCanvasInputMode.DRAW_TERRAIN,
                                            ),
                                        inputMode:
                                            TabletopCanvasInputMode.DRAW_TERRAIN,
                                    });
                                }}
                                color={
                                    canvasInputState.inputMode ===
                                    TabletopCanvasInputMode.DRAW_TERRAIN
                                        ? "active"
                                        : undefined
                                }
                                icon={drawPolygonIcon}
                                tooltip="Draw Terrain"
                            />
                        )}
                        {canvasInputState.inputDomain ===
                            TabletopCanvasInputDomain.TOKENS && (
                            <Button
                                onClick={() => {
                                    setCanvasInputState({
                                        ...canvasInputState,
                                        inputDomain:
                                            fromTabletopCanvasInputMode(
                                                TabletopCanvasInputMode.MANIPULATE_TOKEN,
                                            ),
                                        inputMode:
                                            TabletopCanvasInputMode.MANIPULATE_TOKEN,
                                    });
                                }}
                                color={
                                    canvasInputState.inputMode ===
                                    TabletopCanvasInputMode.MANIPULATE_TOKEN
                                        ? "active"
                                        : undefined
                                }
                                icon={selectItemIcon}
                                tooltip="Manipulate Tokens"
                            />
                        )}
                        {canvasInputState.inputDomain ===
                            TabletopCanvasInputDomain.TOKENS && (
                            <Button
                                onClick={() => {
                                    setCanvasInputState({
                                        ...canvasInputState,
                                        inputDomain:
                                            fromTabletopCanvasInputMode(
                                                TabletopCanvasInputMode.CREATE_TOKEN,
                                            ),
                                        inputMode:
                                            TabletopCanvasInputMode.CREATE_TOKEN,
                                    });
                                }}
                                color={
                                    canvasInputState.inputMode ===
                                    TabletopCanvasInputMode.CREATE_TOKEN
                                        ? "active"
                                        : undefined
                                }
                                icon={tokenAddIcon}
                                tooltip="Create Token"
                            />
                        )}
                    </Flexbox>
                    {canvasInputState.inputMode ===
                        TabletopCanvasInputMode.DRAW_TERRAIN && <hr />}
                    {canvasInputState.inputMode ===
                        TabletopCanvasInputMode.DRAW_TERRAIN && (
                        <Flexbox direction="row" wrap={true}>
                            {terrainRenderers.flatMap((r) => {
                                if (r instanceof TabletopSimpleShapeRenderer) {
                                    const img = r.fillTextureSrc;

                                    if (img != null) {
                                        return (
                                            <Button
                                                key={r.id}
                                                color={
                                                    canvasInputState.palette
                                                        .terrainShape?.id ===
                                                    r.id
                                                        ? "active"
                                                        : "neutral"
                                                }
                                                icon={img}
                                                iconSize={16}
                                                onClick={() => {
                                                    setCanvasInputState({
                                                        ...canvasInputState,
                                                        palette: {
                                                            ...canvasInputState.palette,
                                                            terrainShape: r,
                                                        },
                                                    });
                                                }}
                                            />
                                        );
                                    }
                                }

                                return [];
                            })}
                        </Flexbox>
                    )}
                    <div style={{ height: "100%" }} />
                </Flexbox>
            </Flexbox>
            {showLoadCharacterModal && (
                <LoadCharacterModal
                    isVisible={showLoadCharacterModal}
                    onClose={() => {
                        setShowLoadCharacterModal(false);
                    }}
                    onConfirm={(characterID) => {}}
                />
            )}
        </div>
    );
}
